import { useCallback, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { z, ZodTypeAny } from "zod";
import isEqual from "lodash.isequal";
import { apiRequest } from "@shared/services/api/apiRequest";
import { quizEventBus } from "../../quiz.events";
import { packAnswerPayload } from "../../utils/packAnswerPayload";
import { makeQuizQuestionInputKey } from "../../utils/makeQuizQuestionInputKey";
import { packFormDefaultValuesFromQuiz } from "../../utils/packFormDefaultValuesFromQuiz";
import {
  QuizQuestion,
  QuizAnswerPayload,
  QuizResponse,
  QuizFormData,
} from "@platform/types/quiz.types";

type Args = {
  quiz: QuizResponse | undefined;
  quizResponseId: number;
  resetIndexKey?: any;
  onCompleteQuiz: () => void;
};

export function useQuizProps({
  quiz,
  quizResponseId,
  resetIndexKey,
  onCompleteQuiz,
}: Args) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReady =
    !!quiz && Array.isArray(quiz.questionSets) && quiz.questionSets.length > 0;

  const isGatedLinear = isReady && quiz.type === "gated linear";

  const defaultButtonLabel = isGatedLinear ? "Yes, continue" : "Continue";

  const [buttonLabel, setButtonLabel] = useState(defaultButtonLabel);

  const [formData, setFormData] = useState<QuizFormData>({});

  const [currentQuestionObjectIndex, setCurrentQuestionObjectIndex] =
    useState(0);

  const questionObject = isReady
    ? isGatedLinear
      ? quiz.questionSets[0]
      : quiz.questionSets
    : [];

  const questions = isGatedLinear
    ? questionObject?.questions ?? []
    : questionObject?.[currentQuestionObjectIndex]?.questions ?? [];

  // set form default data when quiz changes
  useEffect(() => {
    if (!quiz) return;
    if (!quiz.questions) return;

    setFormData(packFormDefaultValuesFromQuiz(quiz));
  }, [quiz]);

  useEffect(() => {
    setCurrentQuestionObjectIndex(0);
    setButtonLabel(defaultButtonLabel);
  }, [quiz, resetIndexKey]);

  const mutation = useMutation({
    mutationFn: async (answerPayload: QuizAnswerPayload) => {
      return await apiRequest("/api/response-answer", "POST", answerPayload);
    },
    onError: (error) => {
      setIsSubmitting(false);
    },
    onSuccess: (data, variables) => {
      const { question, answer_text, selected_quiz_choices } = variables;

      const responseId = data.quiz_response.id;

      quizEventBus.emitEvent("quiz:answer:submitted", {
        questionId: question,
        answer: answer_text || selected_quiz_choices,
        quizId: quiz?.id,
        responseId,
      });
    },
  });

  const handleNextQuestionObject = async (data: z.infer<ZodTypeAny>) => {
    if (!isReady || !quiz) return;

    setIsSubmitting(true);

    const previousFormData = { ...formData };

    setFormData((prev) => ({ ...prev, ...data }));

    if (!isGatedLinear) {
      for (const [fieldName, answer] of Object.entries(data)) {
        const question = questions.find(
          (q) => makeQuizQuestionInputKey(q.id) === fieldName
        );

        if (!question) continue;

        const answerPayload = packAnswerPayload({
          question,
          answer,
          quizResponseId,
        });

        if (!isEqual(previousFormData[fieldName], answer) && answer !== "") {
          await mutation.mutateAsync(answerPayload);

          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      // if in a dialog, refocus on the heading after the mutation is complete
      const dialogHeading =
        document.querySelector<HTMLDivElement>("#dialog-title");

      if (dialogHeading) {
        dialogHeading.focus();
      }

      setIsSubmitting(false);
    }

    const totalSteps = isGatedLinear
      ? questionObject?.questions?.length - 1 ?? 0
      : questionObject?.length - 1 ?? 0;

    if (currentQuestionObjectIndex < totalSteps) {
      setTimeout(() => {
        setCurrentQuestionObjectIndex((prev) => prev + 1);
      }, 25);

      if (currentQuestionObjectIndex === totalSteps - 1) {
        setButtonLabel("Submit");
      }
    } else {
      onCompleteQuiz?.();
    }
  };

  const handlePreviousQuestionObject = () => {
    setCurrentQuestionObjectIndex((prev) => prev - 1);
  };

  const filterQuestionSet = useCallback(
    (question: QuizQuestion, idx: number) =>
      isGatedLinear ? idx === currentQuestionObjectIndex : true,
    [currentQuestionObjectIndex, resetIndexKey, isGatedLinear]
  );

  const activeQuestionSet = isGatedLinear
    ? questionObject
    : questionObject?.[currentQuestionObjectIndex] ?? { questions: [] };

  const title = isGatedLinear
    ? questionObject?.header || quiz?.title || ""
    : activeQuestionSet?.header || quiz?.title || "";

  const description = isGatedLinear
    ? questionObject?.description || quiz?.description || ""
    : activeQuestionSet?.description || quiz?.description || "";

  const stepIndex = currentQuestionObjectIndex;
  const totalSteps = isGatedLinear
    ? questionObject?.questions?.length ?? 0
    : questionObject?.length ?? 0;

  return {
    activeQuestionSet,
    buttonLabel,
    filterQuestionSet,
    handleNextQuestionObject,
    handlePreviousQuestionObject,
    formData,
    title,
    description,
    stepIndex,
    totalSteps,
    isSubmitting,
  };
}

export default useQuizProps;
